import React from 'react'
import PropTypes from 'prop-types'
import styled from 'styled-components'
import PacingRing from './PacingRing'
import { formatCurrency } from '../mocks/wipAiFixture'

const Section = styled.section`
  margin-top: 28px;
`

const KpiGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(5, 1fr);
  gap: 12px;
`

const Card = styled.div`
  background: ${p => p.theme.colors.semantic.surface};
  border: 1px solid ${p => p.theme.colors.semantic.border};
  border-radius: 12px;
  box-shadow: ${p => p.theme.colors.semantic.shadowSm};
  padding: 16px 18px;
  display: flex;
  flex-direction: column;
  gap: 4px;
  min-height: 110px;
`

const Lbl = styled.div`
  font-family: ${p => p.theme.fontFamily.sans.join(', ')};
  font-size: 10px;
  font-weight: 700;
  letter-spacing: 0.14em;
  text-transform: uppercase;
  color: ${p => p.theme.colors.semantic.textMuted};
`

const Val = styled.div`
  font-family: ${p => p.theme.fontFamily.sans.join(', ')};
  font-size: 22px;
  font-weight: 700;
  letter-spacing: -0.01em;
  font-variant-numeric: tabular-nums;
  margin-top: 2px;
  color: ${p => p.$c || p.theme.colors.semantic.textPrimary};
`

const Sub = styled.div`
  font-size: 11px;
  color: ${p => p.theme.colors.semantic.textMuted};
  margin-top: auto;
  b {
    color: ${p => p.theme.colors.semantic.textPrimary};
    font-weight: 600;
  }
`

const RingWrap = styled.div`
  display: flex;
  align-items: center;
  gap: 14px;
  margin-top: 2px;
`

const StatusLbl = styled.div`
  font-family: ${p => p.theme.fontFamily.sans.join(', ')};
  font-size: 13px;
  font-weight: 700;
  color: ${p => p.$c};
`

const Delta = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 3px;
  font-family: ${p => p.theme.fontFamily.sans.join(', ')};
  font-size: 11px;
  font-weight: 700;
  font-variant-numeric: tabular-nums;
  color: ${p => (p.$up ? p.theme.colors.semantic.danger : p.theme.colors.semantic.success)};
`

const TrendPill = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  align-self: flex-start;
  height: 24px;
  padding: 0 10px;
  border-radius: 6px;
  font-size: 11px;
  font-weight: 700;
  font-family: ${p => p.theme.fontFamily.sans.join(', ')};
  letter-spacing: 0.04em;
  text-transform: uppercase;
  background: ${p => p.$bg};
  color: ${p => p.$fg};
`

const trendStyle = t => {
  if (t === 'On Target') return { bg: '#E6F5EE', fg: '#00A162' }
  if (t === 'Ahead') return { bg: '#EEEEFF', fg: '#4241BF' }
  if (t === 'Behind') return { bg: '#FBF1DE', fg: '#D98E00' }
  return { bg: '#FBE7E5', fg: '#D0342C' }
}

const WipAiKpiStrip = ({ rpt, iso, forecast, trend, pacingUsd, monthLabelText }) => {
  const pacingPct = parseFloat(String(rpt.wipPacing || '0').replace('%', '')) || 0
  const variance = (Number(rpt.totalFeesAndExpenses) || 0) - (Number(rpt.budget) || 0)
  const budget = Number(rpt.budget) || 0
  const tp = trendStyle(trend)
  const delta = Number(pacingUsd) || 0
  const overPace = delta > 0
  const paceLabelColor = overPace ? '#D0342C' : delta < 0 ? '#00A162' : '#050519'

  return (
    <Section id="kpi">
      <KpiGrid>
        <Card>
          <Lbl>Monthly Budget</Lbl>
          <Val>{formatCurrency(rpt.budget, iso)}</Val>
          <Sub>
            For <b>{monthLabelText}</b>
          </Sub>
        </Card>
        <Card>
          <Lbl>Actuals to Date</Lbl>
          <Val>{formatCurrency(rpt.totalFeesAndExpenses, iso)}</Val>
          <Sub>
            <b>{rpt.submittedHours?.staffHours ?? 0}</b> hrs logged
          </Sub>
        </Card>
        <Card>
          <Lbl>Pacing (USD)</Lbl>
          <Val $c={paceLabelColor}>
            {delta > 0 ? '+' : ''}
            {formatCurrency(delta, iso)}
          </Val>
          <RingWrap>
            <PacingRing pct={pacingPct} />
            <div>
              <StatusLbl $c={paceLabelColor}>
                {overPace ? 'Over pace' : delta < 0 ? 'Under pace' : 'On pace'}
              </StatusLbl>
              <Sub style={{ marginTop: 2 }}>
                Reported <b>{Math.round(pacingPct)}%</b> vs target
              </Sub>
            </div>
          </RingWrap>
        </Card>
        <Card>
          <Lbl>Variance</Lbl>
          <Val $c={variance > 0 ? '#D0342C' : '#00A162'}>
            {variance > 0 ? '+' : ''}
            {formatCurrency(variance, iso)}
          </Val>
          <Sub>
            <Delta $up={variance > 0}>
              {budget === 0
                ? '—'
                : `${variance > 0 ? '▲' : '▼'} ${Math.abs(Math.round((variance / budget) * 100))}%`}
            </Delta>
            {budget !== 0 && <span style={{ marginLeft: 6 }}>vs budget</span>}
          </Sub>
        </Card>
        <Card>
          <Lbl>Forecast EOM</Lbl>
          <Val>{formatCurrency(forecast, iso)}</Val>
          <Sub>
            <TrendPill $bg={tp.bg} $fg={tp.fg}>
              {trend}
            </TrendPill>
          </Sub>
        </Card>
      </KpiGrid>
    </Section>
  )
}

WipAiKpiStrip.propTypes = {
  rpt: PropTypes.object.isRequired,
  iso: PropTypes.string.isRequired,
  forecast: PropTypes.number.isRequired,
  trend: PropTypes.string.isRequired,
  pacingUsd: PropTypes.number.isRequired,
  monthLabelText: PropTypes.string.isRequired,
}

export default WipAiKpiStrip
